import type { MatchConfig } from '../../core/contracts';
import { h } from '../dom';
import type { ScreenCtx } from './context';
import { fixtureCard, screenHeader, segmented, type ScreenInstance } from './common';
import type { Difficulty } from '../../meta/career';

const randSeed = () => (Math.random() * 0xffffffff) >>> 0;
const FIXTURES = 6;

export function quickScreen(ctx: ScreenCtx): ScreenInstance {
  const prefs = ctx.store.get().quick;
  let difficulty: Difficulty = prefs.difficulty;
  let half = prefs.halfLengthSeconds;
  let fixtures: MatchConfig[] = [];
  let selected = 0;

  const grid = h('div', { class: 'sd-quick__grid' });
  const seedInput = h('input', { class: 'sd-input', attrs: { type: 'text', inputmode: 'numeric', placeholder: 'ENTER SEED', maxlength: 10, spellcheck: 'false', 'aria-label': 'Seed' } });

  const render = () => {
    grid.textContent = '';
    fixtures.forEach((cfg, i) => {
      const card = fixtureCard(cfg, { selected: i === selected, onClick: () => { selected = i; render(); } });
      card.style.animationDelay = `${i * 0.05}s`;
      grid.append(card);
    });
  };
  const shuffle = (custom?: number) => {
    const seeds = custom !== undefined ? [custom, ...Array.from({ length: FIXTURES - 1 }, randSeed)] : Array.from({ length: FIXTURES }, randSeed);
    fixtures = seeds.map((s) => ctx.deps.generateMatchConfig(s));
    selected = 0;
    render();
  };
  shuffle();

  const loadSeed = () => {
    const n = Number(seedInput.value.replace(/\D/g, ''));
    if (!seedInput.value.trim() || !Number.isFinite(n)) { seedInput.classList.add('is-error'); setTimeout(() => seedInput.classList.remove('is-error'), 500); return; }
    shuffle(n >>> 0);
    ctx.notify(`SEED ${n >>> 0} LOADED`);
  };
  seedInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { loadSeed(); e.stopPropagation(); } });

  const savePrefs = () => ctx.store.update((d) => { d.quick = { difficulty, halfLengthSeconds: half }; });
  const diff = segmented<Difficulty>([
    { value: 'rookie', label: 'ROOKIE', sub: 'Forgiving timing' },
    { value: 'pro', label: 'PRO', sub: 'Broadcast standard' },
    { value: 'legend', label: 'LEGEND', sub: 'Every frame counts' },
  ], difficulty, (v) => { difficulty = v; savePrefs(); }, 'sd-segctl--tall');
  const halfCtl = segmented<string>([
    { value: '180', label: '3 MIN' }, { value: '240', label: '4 MIN' }, { value: '360', label: '6 MIN' },
  ], String(half), (v) => { half = Number(v); savePrefs(); });

  const go = () => ctx.startQuick(fixtures[selected]!.seed, difficulty, half);

  const el = h('section', { class: 'sd-screen sd-page sd-quick' }, [
    screenHeader('QUICK MATCH', { kicker: 'EXHIBITION', onBack: () => ctx.back() }),
    h('div', { class: 'sd-quick__body' }, [
      h('div', { class: 'sd-quick__left' }, [
        h('div', { class: 'sd-section-head' }, [
          h('span', { text: 'CHOOSE A FIXTURE' }),
          h('div', { class: 'sd-quick__tools' }, [
            h('div', { class: 'sd-seedbox' }, [h('span', { class: 'sd-seedbox__label', text: 'SEED' }), seedInput, h('button', { class: 'sd-btn sd-btn--small', attrs: { type: 'button' }, text: 'LOAD', on: { click: loadSeed } })]),
            h('button', { class: 'sd-btn sd-btn--small', attrs: { type: 'button' }, on: { click: () => shuffle() } }, [h('span', { class: 'sd-spin', text: '↻' }), 'NEW FIXTURES']),
          ]),
        ]),
        grid,
      ]),
      h('aside', { class: 'sd-quick__right sd-panel' }, [
        h('div', { class: 'sd-field' }, [h('div', { class: 'sd-field__label', text: 'DIFFICULTY' }), diff.el]),
        h('div', { class: 'sd-field' }, [h('div', { class: 'sd-field__label', text: 'HALF LENGTH' }), halfCtl.el, h('div', { class: 'sd-field__hint', text: 'Real time per half. The match clock still shows 45:00.' })]),
        h('div', { class: 'sd-quick__spacer' }),
        h('button', { class: 'sd-btn sd-btn--primary sd-btn--golive', attrs: { type: 'button' }, on: { click: go } }, [h('span', { class: 'sd-golive__dot' }), 'GO LIVE', h('kbd', { text: 'ENTER' })]),
      ]),
    ]),
  ]);

  return {
    el,
    onKey(e) {
      if (document.activeElement === seedInput) return false;
      if (e.key === 'Enter' && !(document.activeElement instanceof HTMLButtonElement)) { go(); return true; }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        selected = (selected + (e.key === 'ArrowRight' ? 1 : -1) + fixtures.length) % fixtures.length; render(); return true;
      }
      return false;
    },
  };
}
